/**
 * Analyseur sémantique : liste blanche + typage minimal (float/int/bool/Vector2/Vector3).
 *
 * - annote `expr.type` sur chaque expression (l'interpréteur s'en sert pour la division entière
 *   et l'arrondi float32) ;
 * - remonte jusqu'à `MAX_REPORTED_ERRORS` erreurs distinctes (une expression en erreur prend le
 *   type `unknown`, qui n'engendre aucune erreur en cascade) ;
 * - calcule `program.usesDeltaTime` (analyse de flot : `Time.deltaTime` atteint-il
 *   `transform.position` / `transform.Translate` ?).
 */

import type { BinaryOp, Declarator, Expr, LifecycleMethod, Program, SimType, Stmt, TypeRef } from './ast';
import { SimError, syntaxError, unsupportedError } from './errors';
import { MAX_REPORTED_ERRORS } from './parser';
import {
  ALLOWED_AXES, API_ROOTS, DEBUG_METHODS, INPUT_METHODS, KEYCODE_SET, ALLOWED_KEYCODES, KNOWN_UNSUPPORTED,
  MATHF_CONSTANTS, MATHF_FUNCTIONS, MATHF_INT_PRESERVING, TIME_PROPERTIES, TRANSFORM_METHODS, TRANSFORM_PROPERTIES,
  VALUE_TYPES, VECTOR2_STATICS, VECTOR3_STATICS, VECTOR_FUNCTIONS, VECTOR_INSTANCE_MEMBERS, LIFECYCLE_METHODS,
} from './whitelist';

export interface AnalyzeResult {
  errors: SimError[];
  warnings: string[];
}

interface VarInfo {
  type: SimType;
  kind: 'field' | 'local';
  name: string;
  line: number;
  used: boolean;
}

class Scope {
  readonly vars = new Map<string, VarInfo>();
  constructor(readonly parent: Scope | null, readonly isFieldScope: boolean) {}

  lookup(name: string): VarInfo | undefined {
    return this.vars.get(name) ?? this.parent?.lookup(name);
  }

  /** Une variable locale du même nom existe-t-elle dans cette portée ou une portée locale englobante ? */
  hasLocal(name: string): boolean {
    if (this.isFieldScope) return false;
    if (this.vars.has(name)) return true;
    return this.parent?.hasLocal(name) ?? false;
  }

  names(): string[] {
    return [...this.vars.keys(), ...(this.parent?.names() ?? [])];
  }
}

class TooManyErrors extends Error {}

const isNumeric = (t: SimType): boolean => t === 'float' || t === 'int';
const isVector = (t: SimType): boolean => t === 'Vector2' || t === 'Vector3';
const promote = (a: SimType, b: SimType): SimType => (a === 'int' && b === 'int' ? 'int' : 'float');

export function analyze(program: Program): AnalyzeResult {
  const analyzer = new Analyzer(program);
  try {
    analyzer.run();
  } catch (e) {
    if (!(e instanceof TooManyErrors)) throw e;
  }
  return { errors: analyzer.errors, warnings: analyzer.warnings };
}

class Analyzer {
  readonly errors: SimError[] = [];
  readonly warnings: string[] = [];
  private readonly fieldScope = new Scope(null, true);
  private scope: Scope = this.fieldScope;
  private inFieldInitializer = false;

  constructor(private readonly program: Program) {}

  // ------------------------------------------------------------------ signalement

  private error(err: SimError): void {
    this.errors.push(err);
    if (this.errors.length >= MAX_REPORTED_ERRORS) throw new TooManyErrors();
  }

  private unsupported(symbol: string, line: number, column: number, fallback: string, lookupKey = symbol, fallbackHint?: string): void {
    const known = KNOWN_UNSUPPORTED[lookupKey];
    this.error(unsupportedError(symbol, known?.message ?? fallback, line, column, known?.hint ?? fallbackHint));
  }

  /** Nom de la racine d'API (`transform`, `Time`…) si `expr` en est une (et n'est pas masquée), sinon `null`. */
  private rootOf(expr: Expr): string | null {
    return expr.kind === 'ident' && API_ROOTS.has(expr.name) && !this.scope.lookup(expr.name) ? expr.name : null;
  }

  // ------------------------------------------------------------------ programme

  run(): void {
    const { program } = this;

    for (const field of program.fields) {
      if (API_ROOTS.has(field.name)) {
        this.error(syntaxError(`\`${field.name}\` est un nom réservé de l'API Unity : choisis un autre nom de champ.`, field.line, field.column));
        continue;
      }
      if (this.fieldScope.vars.has(field.name)) {
        this.error(syntaxError(`Le champ \`${field.name}\` est déclaré deux fois.`, field.line, field.column, { csharpCode: 'CS0102' }));
        continue;
      }
      const type = this.resolveType(field.typeRef);
      this.fieldScope.vars.set(field.name, { type, kind: 'field', name: field.name, line: field.line, used: false });
      if (field.init) {
        this.inFieldInitializer = true;
        const valueType = this.typeOf(field.init);
        this.inFieldInitializer = false;
        this.checkAssignable(valueType, type, field.init, field.name);
      }
    }

    for (const name of LIFECYCLE_METHODS) {
      const method = program.methods[name];
      if (!method) continue;
      this.scope = new Scope(this.fieldScope, false);
      this.analyzeBlockBody(method.body.body);
      this.scope = this.fieldScope;
    }

    for (const info of this.fieldScope.vars.values()) {
      if (!info.used) this.warnings.push(`Ligne ${info.line}: le champ \`${info.name}\` n'est jamais utilisé.`);
    }

    program.usesDeltaTime = computeUsesDeltaTime(program);
  }

  private resolveType(ref: TypeRef): SimType {
    if (ref.generic || ref.array || ref.base === 'var') return 'unknown'; // déjà signalé par le parseur
    const known = VALUE_TYPES[ref.base];
    if (known) return known;
    if (ref.base === 'string') {
      this.unsupported('string', ref.line, ref.column, 'Le type `string` n\'est pas au programme de cet exercice.');
    } else if (ref.base === 'double') {
      this.unsupported('double', ref.line, ref.column, 'Utilise `float`.');
    } else {
      this.unsupported(ref.text, ref.line, ref.column,
        `Le type \`${ref.text}\` n'est pas au programme de cet exercice : seuls \`float\`, \`int\`, \`bool\`, \`Vector2\` et \`Vector3\` sont disponibles.`,
        ref.base);
    }
    return 'unknown';
  }

  // ------------------------------------------------------------------ instructions

  private analyzeBlockBody(body: Stmt[]): void {
    for (const stmt of body) this.analyzeStmt(stmt);
  }

  private withScope(fn: () => void): void {
    const parent = this.scope;
    this.scope = new Scope(parent, false);
    try {
      fn();
    } finally {
      for (const info of this.scope.vars.values()) {
        if (!info.used) this.warnings.push(`Ligne ${info.line}: la variable \`${info.name}\` est déclarée mais jamais utilisée.`);
      }
      this.scope = parent;
    }
  }

  private analyzeStmt(stmt: Stmt): void {
    switch (stmt.kind) {
      case 'block':
        this.withScope(() => this.analyzeBlockBody(stmt.body));
        return;
      case 'vardecl':
        this.declareLocals(stmt.typeRef, stmt.declarators);
        return;
      case 'expr':
        this.typeOf(stmt.expr);
        return;
      case 'if':
        this.checkCondition(stmt.test, 'if');
        this.analyzeStmt(stmt.consequent);
        if (stmt.alternate) this.analyzeStmt(stmt.alternate);
        return;
      case 'while':
        this.checkCondition(stmt.test, 'while');
        this.analyzeStmt(stmt.body);
        return;
      case 'for':
        this.withScope(() => {
          if (stmt.init) this.analyzeStmt(stmt.init);
          if (stmt.test) this.checkCondition(stmt.test, 'for');
          for (const u of stmt.update) this.typeOf(u);
          this.analyzeStmt(stmt.body);
        });
        return;
      default:
        return;
    }
  }

  private declareLocals(typeRef: TypeRef, declarators: Declarator[]): void {
    const type = this.resolveType(typeRef);
    for (const d of declarators) {
      if (API_ROOTS.has(d.name)) {
        this.error(syntaxError(`\`${d.name}\` est un nom réservé de l'API Unity : choisis un autre nom de variable.`, d.line, d.column));
        continue;
      }
      if (this.scope.hasLocal(d.name)) {
        this.error(syntaxError(`La variable \`${d.name}\` est déjà déclarée dans cette portée (ou une portée englobante) : ne remets pas le type devant, écris simplement \`${d.name} = …\`.`, d.line, d.column, { csharpCode: 'CS0128' }));
        continue;
      }
      let valueType: SimType | null = null;
      if (d.init) valueType = this.typeOf(d.init);
      this.scope.vars.set(d.name, { type, kind: 'local', name: d.name, line: d.line, used: false });
      if (d.init && valueType) this.checkAssignable(valueType, type, d.init, d.name);
    }
  }

  private checkCondition(test: Expr, keyword: string): void {
    const t = this.typeOf(test);
    if (t !== 'bool' && t !== 'unknown') {
      this.error(syntaxError(
        `La condition du \`${keyword}\` doit être un \`bool\`, pas un \`${t}\` : impossible de convertir implicitement \`${t}\` en \`bool\`.`,
        test.line, test.column,
        { csharpCode: 'CS0029', hint: isNumeric(t) ? 'Compare explicitement : `if (h != 0f)` ou `if (speed > 0f)`.' : undefined },
      ));
    }
  }

  private checkAssignable(from: SimType, to: SimType, at: Expr, targetName: string): boolean {
    if (from === 'unknown' || to === 'unknown' || from === to) return true;
    if (from === 'int' && to === 'float') return true;
    if (isVector(from) && isVector(to)) return true; // conversions implicites Unity Vector2 <-> Vector3
    if (from === 'float' && to === 'int') {
      this.error(syntaxError(
        `Impossible de convertir implicitement un \`float\` en \`int\` (\`${targetName}\`) : la partie décimale serait perdue, C# refuse.`,
        at.line, at.column,
        { csharpCode: 'CS0266', hint: `Déclare \`${targetName}\` en \`float\`, ou convertis explicitement : \`(int)valeur\`.` },
      ));
      return false;
    }
    if (from === 'void') {
      this.error(syntaxError(`Cette expression ne renvoie rien (\`void\`) : impossible de l'affecter à \`${targetName}\`.`, at.line, at.column, { csharpCode: 'CS0029' }));
      return false;
    }
    if (from === 'string') {
      this.error(syntaxError(`Une chaîne de caractères ne peut pas être affectée à \`${targetName}\` (\`${to}\`) ; les chaînes ne servent qu'à \`Debug.Log(...)\`.`, at.line, at.column, { csharpCode: 'CS0029' }));
      return false;
    }
    const hint =
      isNumeric(from) && isVector(to)
        ? `Un vecteur se construit avec \`new ${to}(…)\` ou \`${to}.right * valeur\`.`
        : isVector(from) && isNumeric(to)
          ? `Pour une composante, lis \`.x\` / \`.y\` ; pour une longueur, \`.magnitude\`.`
          : undefined;
    this.error(syntaxError(`Impossible de convertir implicitement \`${from}\` en \`${to}\` pour \`${targetName}\`.`, at.line, at.column, { csharpCode: 'CS0029', hint }));
    return false;
  }

  // ------------------------------------------------------------------ expressions

  private typeOf(expr: Expr): SimType {
    const t = this.computeType(expr);
    expr.type = t;
    return t;
  }

  private computeType(expr: Expr): SimType {
    switch (expr.kind) {
      case 'error':
        return 'unknown';
      case 'literal':
        if (expr.literalType === 'double') {
          this.error(syntaxError(
            `Le littéral \`${expr.raw}\` est un \`double\` en C#, pas un \`float\` : ajoute le suffixe \`f\` → \`${expr.raw.replace(/[dD]$/, '')}f\`.`,
            expr.line, expr.column,
            { csharpCode: 'CS0664', hint: 'En Unity, tous les nombres décimaux du gameplay sont des `float` : `5f`, `0.5f`, `2.25f`.' },
          ));
          return 'float';
        }
        return expr.literalType;
      case 'ident':
        return this.typeOfIdent(expr);
      case 'member':
        return this.typeOfMember(expr, false);
      case 'call':
        return this.typeOfCall(expr);
      case 'new':
        return this.typeOfNew(expr);
      case 'unary':
        return this.typeOfUnary(expr);
      case 'binary':
        return this.binaryType(expr.op, this.typeOf(expr.left), this.typeOf(expr.right), expr);
      case 'assign':
        return this.typeOfAssign(expr);
      case 'update': {
        const t = this.typeOfAssignTarget(expr.target, expr.op);
        if (t === 'unknown') return 'unknown';
        if (!isNumeric(t)) {
          this.error(syntaxError(`\`${expr.op}\` ne s'applique qu'à un nombre (\`int\` ou \`float\`), pas à un \`${t}\`.`, expr.line, expr.column, { csharpCode: 'CS0023' }));
          return 'unknown';
        }
        return t;
      }
      case 'conditional': {
        const t = this.typeOf(expr.test);
        if (t !== 'bool' && t !== 'unknown') {
          this.error(syntaxError(`La condition avant \`?\` doit être un \`bool\`, pas un \`${t}\`.`, expr.test.line, expr.test.column, { csharpCode: 'CS0029' }));
        }
        const a = this.typeOf(expr.consequent);
        const b = this.typeOf(expr.alternate);
        if (a === 'unknown' || b === 'unknown') return 'unknown';
        if (a === b) return a;
        if (isNumeric(a) && isNumeric(b)) return 'float';
        if (isVector(a) && isVector(b)) return 'Vector3';
        this.error(syntaxError(`Les deux branches de \`? :\` doivent avoir le même type (\`${a}\` et \`${b}\` ici).`, expr.line, expr.column, { csharpCode: 'CS0173' }));
        return 'unknown';
      }
      case 'cast': {
        const t = this.typeOf(expr.expr);
        if (t === 'unknown') return expr.typeName;
        if (!isNumeric(t)) {
          this.error(syntaxError(`Impossible de convertir un \`${t}\` en \`${expr.typeName}\`.`, expr.line, expr.column, { csharpCode: 'CS0030' }));
          return 'unknown';
        }
        return expr.typeName;
      }
      default:
        return 'unknown';
    }
  }

  private typeOfIdent(expr: Expr & { kind: 'ident' }): SimType {
    const info = this.scope.lookup(expr.name);
    if (info) {
      if (this.inFieldInitializer && info.kind === 'field') {
        this.error(syntaxError(
          `Un initialiseur de champ ne peut pas utiliser un autre champ (\`${expr.name}\`) : C# l'interdit.`,
          expr.line, expr.column,
          { csharpCode: 'CS0236', hint: 'Fais ce calcul dans `Start()`.' },
        ));
        return 'unknown';
      }
      info.used = true;
      return info.type;
    }
    if (API_ROOTS.has(expr.name)) {
      if (this.inFieldInitializer && expr.name === 'transform') {
        this.error(syntaxError('`transform` n\'est pas disponible dans un initialiseur de champ : lis-le dans `Start()` ou `Update()`.', expr.line, expr.column, { csharpCode: 'CS0236' }));
        return 'unknown';
      }
      this.error(syntaxError(`\`${expr.name}\` doit être suivi d'un point et d'un membre : \`${expr.name}.…\` (exemple : \`${exampleFor(expr.name)}\`).`, expr.line, expr.column, { csharpCode: 'CS0119' }));
      return 'unknown';
    }
    if (KNOWN_UNSUPPORTED[expr.name]) {
      this.unsupported(expr.name, expr.line, expr.column, `\`${expr.name}\` n'est pas au programme de cet exercice.`);
      return 'unknown';
    }
    const suggestion = suggest(expr.name, [...this.scope.names(), ...API_ROOTS]);
    this.error(syntaxError(
      `Le nom \`${expr.name}\` n'existe pas dans ce contexte : il n'est ni un champ, ni une variable déclarée, ni une API disponible ici.`,
      expr.line, expr.column,
      { csharpCode: 'CS0103', hint: suggestion ? `Tu voulais peut-être écrire \`${suggestion}\` ?` : 'Déclare la variable avant de l\'utiliser : `float h = …;`' },
    ));
    return 'unknown';
  }

  private typeOfMember(expr: Expr & { kind: 'member' }, asCallee: boolean): SimType {
    const { object, name } = expr;
    const root = this.rootOf(object);
    if (root) {
      const key = `${root}.${name}`;
      if (this.inFieldInitializer && root === 'transform') {
        this.error(syntaxError('`transform` n\'est pas disponible dans un initialiseur de champ : lis-le dans `Start()` ou `Update()`.', expr.line, expr.column, { csharpCode: 'CS0236' }));
        return 'unknown';
      }
      switch (root) {
        case 'transform': {
          const prop = TRANSFORM_PROPERTIES[name];
          if (prop) return prop;
          if (TRANSFORM_METHODS.has(name)) {
            if (asCallee) return 'void';
            this.error(syntaxError(`\`transform.${name}\` est une méthode : appelle-la avec des parenthèses, \`transform.${name}(…)\`.`, expr.line, expr.column, { csharpCode: 'CS0428' }));
            return 'unknown';
          }
          return this.unsupportedMember(key, expr, ['position', 'Translate'], `Seul \`transform.position\` (et \`transform.Translate(...)\`) est disponible dans cet exercice.`);
        }
        case 'Time': {
          const prop = TIME_PROPERTIES[name];
          if (prop) return prop;
          return this.unsupportedMember(key, expr, Object.keys(TIME_PROPERTIES), 'Membres de `Time` disponibles ici : `deltaTime`, `time`, `frameCount`.');
        }
        case 'Input': {
          if (INPUT_METHODS.has(name)) {
            if (asCallee) return 'unknown';
            this.error(syntaxError(`\`Input.${name}\` est une méthode : appelle-la avec des parenthèses, \`Input.${name}(…)\`.`, expr.line, expr.column, { csharpCode: 'CS0428' }));
            return 'unknown';
          }
          return this.unsupportedMember(key, expr, [...INPUT_METHODS], 'Entrées disponibles ici : `Input.GetAxis`, `Input.GetAxisRaw`, `Input.GetKey`, `Input.GetKeyDown`, `Input.GetKeyUp`.');
        }
        case 'Mathf': {
          if (name in MATHF_CONSTANTS) return 'float';
          if (name in MATHF_FUNCTIONS) {
            if (asCallee) return 'unknown';
            this.error(syntaxError(`\`Mathf.${name}\` est une fonction : appelle-la avec des parenthèses.`, expr.line, expr.column, { csharpCode: 'CS0428' }));
            return 'unknown';
          }
          return this.unsupportedMember(key, expr, [...Object.keys(MATHF_FUNCTIONS), ...Object.keys(MATHF_CONSTANTS)], `Fonctions \`Mathf\` disponibles ici : ${Object.keys(MATHF_FUNCTIONS).join(', ')}.`);
        }
        case 'Vector3':
        case 'Vector2': {
          const statics = root === 'Vector3' ? VECTOR3_STATICS : VECTOR2_STATICS;
          if (statics.includes(name)) return root;
          if (name in VECTOR_FUNCTIONS) {
            if (asCallee) return 'unknown';
            this.error(syntaxError(`\`${key}\` est une fonction : appelle-la avec des parenthèses.`, expr.line, expr.column, { csharpCode: 'CS0428' }));
            return 'unknown';
          }
          return this.unsupportedMember(key, expr, [...statics, ...Object.keys(VECTOR_FUNCTIONS)], `Constantes \`${root}\` disponibles ici : ${statics.join(', ')}.`);
        }
        case 'KeyCode': {
          if (KEYCODE_SET.has(name)) return 'keycode';
          return this.unsupportedMember(key, expr, [...ALLOWED_KEYCODES], `Touches simulées dans cet exercice : ${ALLOWED_KEYCODES.map((k) => `KeyCode.${k}`).join(', ')}.`);
        }
        case 'Debug': {
          if (DEBUG_METHODS.has(name)) {
            if (asCallee) return 'void';
            this.error(syntaxError(`\`Debug.${name}\` est une méthode : \`Debug.${name}("message")\`.`, expr.line, expr.column, { csharpCode: 'CS0428' }));
            return 'unknown';
          }
          return this.unsupportedMember(key, expr, [...DEBUG_METHODS], 'Seul `Debug.Log(...)` (et LogWarning / LogError) est disponible ici.');
        }
        default:
          return 'unknown';
      }
    }

    const objType = this.typeOf(object);
    if (objType === 'unknown') return 'unknown';
    if (isVector(objType)) {
      const member = VECTOR_INSTANCE_MEMBERS[name];
      if (member) {
        if (name === 'z' && objType === 'Vector2') {
          this.error(syntaxError('Un `Vector2` n\'a que `x` et `y`, pas de `z`.', expr.line, expr.column, { csharpCode: 'CS1061', hint: 'Utilise un `Vector3` si tu as besoin de trois composantes.' }));
          return 'unknown';
        }
        return member === 'self' ? objType : 'float';
      }
      if (['Normalize', 'Set', 'Scale'].includes(name)) {
        this.unsupported(`.${name}()`, expr.line, expr.column, `La méthode \`.${name}()\` n'est pas au programme de cet exercice.`, name, name === 'Normalize' ? 'Utilise la propriété `.normalized` : `Vector3 dir = v.normalized;`' : undefined);
        return 'unknown';
      }
      const suggestion = suggest(name, Object.keys(VECTOR_INSTANCE_MEMBERS));
      this.error(syntaxError(`\`${objType}\` ne contient pas de membre \`${name}\`.`, expr.line, expr.column, { csharpCode: 'CS1061', hint: suggestion ? `Tu voulais peut-être écrire \`.${suggestion}\` ?` : 'Membres disponibles : x, y, z, magnitude, sqrMagnitude, normalized.' }));
      return 'unknown';
    }
    this.error(syntaxError(
      `Un \`${objType}\` n'a pas de membre \`${name}\`${isNumeric(objType) ? ' : c\'est déjà un simple nombre' : ''}.`,
      expr.line, expr.column, { csharpCode: 'CS1061' },
    ));
    return 'unknown';
  }

  private unsupportedMember(key: string, expr: Expr, candidates: string[], available: string): SimType {
    const [root, name] = key.split('.');
    const suggestion = suggest(name, candidates);
    if (suggestion && !KNOWN_UNSUPPORTED[key]) {
      this.error(syntaxError(`\`${key}\` n'existe pas.`, expr.line, expr.column, { csharpCode: 'CS0117', hint: `Tu voulais peut-être écrire \`${root}.${suggestion}\` ? (attention aux majuscules)` }));
      return 'unknown';
    }
    this.unsupported(key, expr.line, expr.column, `\`${key}\` n'est pas au programme de cet exercice. ${available}`, key, KNOWN_UNSUPPORTED[key] ? undefined : available);
    return 'unknown';
  }

  private typeOfCall(expr: Expr & { kind: 'call' }): SimType {
    const { callee, args } = expr;

    if (callee.kind === 'error') {
      for (const a of args) this.typeOf(a);
      return 'unknown';
    }

    if (callee.kind === 'ident') {
      const name = callee.name;
      for (const a of args) this.typeOf(a);
      if (LIFECYCLE_METHODS.includes(name as LifecycleMethod)) {
        this.error(syntaxError(`N'appelle pas \`${name}()\` toi-même : c'est Unity qui l'appelle automatiquement.`, expr.line, expr.column));
        return 'unknown';
      }
      if (this.scope.lookup(name)) {
        this.error(syntaxError(`\`${name}\` est une variable, pas une méthode : retire les parenthèses.`, expr.line, expr.column, { csharpCode: 'CS1955' }));
        return 'unknown';
      }
      if (KNOWN_UNSUPPORTED[name]) {
        this.unsupported(name, expr.line, expr.column, `\`${name}\` n'est pas au programme de cet exercice.`);
        return 'unknown';
      }
      this.error(syntaxError(
        `La méthode \`${name}()\` n'existe pas ici : les méthodes définies par l'étudiant ne sont pas au programme de cet exercice, et les fonctions Unity s'appellent via \`Mathf.\`, \`Input.\`, \`Debug.\` ou \`transform.\`.`,
        expr.line, expr.column, { csharpCode: 'CS0103' },
      ));
      return 'unknown';
    }

    if (callee.kind !== 'member') {
      for (const a of args) this.typeOf(a);
      this.error(syntaxError('Appel invalide : seules les méthodes de l\'API (`transform.Translate`, `Input.GetAxis`, `Mathf.Clamp`, `Debug.Log`…) peuvent être appelées.', expr.line, expr.column, { csharpCode: 'CS0149' }));
      return 'unknown';
    }

    const { object, name } = callee;
    const root = this.rootOf(object);
    if (root) {
      const key = `${root}.${name}`;
      switch (root) {
        case 'transform': {
          if (name !== 'Translate') {
            for (const a of args) this.typeOf(a);
            return this.typeOfMember(callee, true);
          }
          callee.type = 'void';
          const argTypes = args.map((a) => this.typeOf(a));
          if (argTypes.some((t) => t === 'unknown')) return 'void';
          if (argTypes.length === 1 && isVector(argTypes[0])) return 'void';
          if (argTypes.length === 3 && argTypes.every(isNumeric)) return 'void';
          this.error(syntaxError(
            `\`transform.Translate\` attend un vecteur (\`Translate(direction * speed * Time.deltaTime)\`) ou trois nombres (\`Translate(x, y, z)\`), pas (${argTypes.join(', ')}).`,
            expr.line, expr.column, { csharpCode: 'CS1503' },
          ));
          return 'void';
        }
        case 'Input': {
          if (!INPUT_METHODS.has(name)) {
            for (const a of args) this.typeOf(a);
            return this.typeOfMember(callee, true);
          }
          if (name === 'GetAxis' || name === 'GetAxisRaw') {
            callee.type = 'float';
            if (args.length !== 1) {
              this.error(syntaxError(`\`Input.${name}\` attend exactement un argument : le nom de l'axe ("Horizontal" ou "Vertical").`, expr.line, expr.column, { csharpCode: 'CS1501' }));
              return 'float';
            }
            const arg = args[0];
            if (arg.kind !== 'literal' || arg.literalType !== 'string') {
              this.typeOf(arg);
              this.error(syntaxError(`\`Input.${name}\` attend le nom de l'axe entre guillemets : \`Input.${name}("Horizontal")\`.`, arg.line, arg.column, { csharpCode: 'CS1503' }));
              return 'float';
            }
            arg.type = 'string';
            if (!ALLOWED_AXES.has(String(arg.value))) {
              const suggestion = suggest(String(arg.value), [...ALLOWED_AXES]);
              if (suggestion) {
                this.error(syntaxError(`L'axe "${arg.value}" n'existe pas.`, arg.line, arg.column, { hint: `Tu voulais peut-être écrire "${suggestion}" ? (attention aux majuscules)` }));
              } else {
                this.unsupported(`Input.${name}("${arg.value}")`, arg.line, arg.column, `Seuls les axes "Horizontal" et "Vertical" sont simulés dans cet exercice.`);
              }
            }
            return 'float';
          }
          // GetKey / GetKeyDown / GetKeyUp
          callee.type = 'bool';
          if (args.length !== 1) {
            this.error(syntaxError(`\`Input.${name}\` attend exactement un argument : \`KeyCode.UpArrow\` par exemple.`, expr.line, expr.column, { csharpCode: 'CS1501' }));
            return 'bool';
          }
          const t = this.typeOf(args[0]);
          if (t !== 'keycode' && t !== 'unknown') {
            this.error(syntaxError(`\`Input.${name}\` attend une touche \`KeyCode.X\` (${t === 'string' ? 'pas une chaîne' : `pas un \`${t}\``}).`, args[0].line, args[0].column, { csharpCode: 'CS1503', hint: 'Exemple : `Input.GetKey(KeyCode.RightArrow)`.' }));
          }
          return 'bool';
        }
        case 'Mathf': {
          const arity = MATHF_FUNCTIONS[name];
          if (arity === undefined) {
            for (const a of args) this.typeOf(a);
            return this.typeOfMember(callee, true);
          }
          const argTypes = args.map((a) => this.typeOf(a));
          if (args.length !== arity) {
            this.error(syntaxError(`\`Mathf.${name}\` attend ${arity} argument${arity > 1 ? 's' : ''}, pas ${args.length}.`, expr.line, expr.column, { csharpCode: 'CS1501' }));
            return 'float';
          }
          for (let i = 0; i < args.length; i++) {
            if (argTypes[i] !== 'unknown' && !isNumeric(argTypes[i])) {
              this.error(syntaxError(`\`Mathf.${name}\` attend des nombres, pas un \`${argTypes[i]}\` (argument ${i + 1}).`, args[i].line, args[i].column, { csharpCode: 'CS1503' }));
              return 'float';
            }
          }
          const allInt = argTypes.every((t) => t === 'int');
          const result = MATHF_INT_PRESERVING.has(name) && allInt ? 'int' : 'float';
          callee.type = result;
          return result;
        }
        case 'Vector3':
        case 'Vector2': {
          const fn = VECTOR_FUNCTIONS[name];
          if (!fn) {
            for (const a of args) this.typeOf(a);
            return this.typeOfMember(callee, true);
          }
          const argTypes = args.map((a) => this.typeOf(a));
          const result: SimType = fn.returns === 'self' ? root : 'float';
          callee.type = result;
          if (args.length !== fn.arity) {
            this.error(syntaxError(`\`${key}\` attend ${fn.arity} arguments, pas ${args.length}.`, expr.line, expr.column, { csharpCode: 'CS1501' }));
            return result;
          }
          const expected: SimType[] = name === 'ClampMagnitude' ? [root, 'float'] : [root, root];
          for (let i = 0; i < args.length; i++) {
            const t = argTypes[i];
            if (t === 'unknown') continue;
            const ok = expected[i] === 'float' ? isNumeric(t) : isVector(t);
            if (!ok) {
              this.error(syntaxError(`\`${key}\` attend un \`${expected[i]}\` en argument ${i + 1}, pas un \`${t}\`.`, args[i].line, args[i].column, { csharpCode: 'CS1503' }));
              return result;
            }
          }
          return result;
        }
        case 'Debug': {
          if (!DEBUG_METHODS.has(name)) {
            for (const a of args) this.typeOf(a);
            return this.typeOfMember(callee, true);
          }
          callee.type = 'void';
          if (args.length !== 1) {
            this.error(syntaxError(`\`Debug.${name}\` attend exactement un argument : \`Debug.${name}("x = " + transform.position.x)\`.`, expr.line, expr.column, { csharpCode: 'CS1501' }));
            return 'void';
          }
          const t = this.typeOf(args[0]);
          if (t === 'void') {
            this.error(syntaxError('`Debug.Log` ne peut pas afficher une expression qui ne renvoie rien (`void`).', args[0].line, args[0].column, { csharpCode: 'CS1503' }));
          }
          return 'void';
        }
        default:
          for (const a of args) this.typeOf(a);
          return this.typeOfMember(callee, true);
      }
    }

    const objType = this.typeOf(object);
    for (const a of args) this.typeOf(a);
    if (objType === 'unknown') return 'unknown';
    if (isVector(objType)) {
      if (['Normalize', 'Set', 'Scale'].includes(name)) {
        this.unsupported(`.${name}()`, expr.line, expr.column, `La méthode \`.${name}()\` n'est pas au programme de cet exercice.`, name, name === 'Normalize' ? 'Utilise la propriété `.normalized` : `Vector3 dir = v.normalized;`' : undefined);
        return 'unknown';
      }
      this.error(syntaxError(`\`${objType}\` n'a pas de méthode \`${name}()\` ; les membres disponibles sont des propriétés : \`.x\`, \`.y\`, \`.magnitude\`, \`.normalized\` (sans parenthèses).`, expr.line, expr.column, { csharpCode: 'CS1061' }));
      return 'unknown';
    }
    this.error(syntaxError(`Un \`${objType}\` n'a pas de méthode \`${name}()\`.`, expr.line, expr.column, { csharpCode: 'CS1061' }));
    return 'unknown';
  }

  private typeOfNew(expr: Expr & { kind: 'new' }): SimType {
    const argTypes = expr.args.map((a) => this.typeOf(a));
    if (expr.typeName === 'Vector3' || expr.typeName === 'Vector2') {
      const expected = expr.typeName === 'Vector3' ? [2, 3] : [2];
      if (!expected.includes(expr.args.length)) {
        this.error(syntaxError(
          `\`new ${expr.typeName}\` attend ${expr.typeName === 'Vector3' ? '3 valeurs (x, y, z)' : '2 valeurs (x, y)'}, pas ${expr.args.length}.`,
          expr.line, expr.column, { csharpCode: 'CS1729' },
        ));
        return expr.typeName;
      }
      for (let i = 0; i < argTypes.length; i++) {
        if (argTypes[i] !== 'unknown' && !isNumeric(argTypes[i])) {
          this.error(syntaxError(`\`new ${expr.typeName}\` attend des nombres (\`float\`), pas un \`${argTypes[i]}\` en position ${i + 1}.`, expr.args[i].line, expr.args[i].column, { csharpCode: 'CS1503' }));
          return expr.typeName;
        }
      }
      return expr.typeName;
    }
    this.unsupported(`new ${expr.typeName}`, expr.line, expr.column, `\`new ${expr.typeName}(...)\` n'est pas au programme de cet exercice : seuls \`new Vector2(x, y)\` et \`new Vector3(x, y, z)\` sont disponibles.`, expr.typeName);
    return 'unknown';
  }

  private typeOfUnary(expr: Expr & { kind: 'unary' }): SimType {
    const t = this.typeOf(expr.operand);
    if (t === 'unknown') return 'unknown';
    if (expr.op === '!') {
      if (t !== 'bool') {
        this.error(syntaxError(`\`!\` s'applique à un \`bool\`, pas à un \`${t}\`.`, expr.line, expr.column, { csharpCode: 'CS0023' }));
        return 'unknown';
      }
      return 'bool';
    }
    if (isNumeric(t) || isVector(t)) return t;
    this.error(syntaxError(`\`${expr.op}\` ne s'applique pas à un \`${t}\`.`, expr.line, expr.column, { csharpCode: 'CS0023' }));
    return 'unknown';
  }

  private binaryType(op: BinaryOp, l: SimType, r: SimType, expr: Expr): SimType {
    const isCompare = op === '<' || op === '>' || op === '<=' || op === '>=' || op === '==' || op === '!=';
    const isLogic = op === '&&' || op === '||';
    if (l === 'unknown' || r === 'unknown') return isCompare || isLogic ? 'bool' : 'unknown';

    const fail = (hint?: string): SimType => {
      this.error(syntaxError(`L'opérateur \`${op}\` ne peut pas s'appliquer à un \`${l}\` et un \`${r}\`.`, expr.line, expr.column, { csharpCode: 'CS0019', hint }));
      return isCompare || isLogic ? 'bool' : 'unknown';
    };

    if (op === '+' && (l === 'string' || r === 'string')) return 'string';

    switch (op) {
      case '+':
      case '-':
        if (isNumeric(l) && isNumeric(r)) return promote(l, r);
        if (isVector(l) && isVector(r)) {
          if (l !== r) return fail(`Convertis d'abord : \`new Vector3(v.x, v.y, 0f)\` — mélanger \`Vector2\` et \`Vector3\` est ambigu pour C#.`);
          return l;
        }
        if (isVector(l) && isNumeric(r)) return fail(`On n'additionne pas un vecteur et un nombre : utilise \`${l}.right * ${'valeur'}\` ou modifie une composante (\`.x\`).`);
        return fail();
      case '*':
        if (isNumeric(l) && isNumeric(r)) return promote(l, r);
        if (isVector(l) && isNumeric(r)) return l;
        if (isNumeric(l) && isVector(r)) return r;
        if (isVector(l) && isVector(r)) return fail('Deux vecteurs ne se multiplient pas directement en Unity ; multiplie un vecteur par un nombre (`direction * speed`).');
        return fail();
      case '/':
        if (isNumeric(l) && isNumeric(r)) return promote(l, r);
        if (isVector(l) && isNumeric(r)) return l;
        return fail();
      case '%':
        if (isNumeric(l) && isNumeric(r)) return promote(l, r);
        return fail();
      case '<':
      case '>':
      case '<=':
      case '>=':
        if (isNumeric(l) && isNumeric(r)) return 'bool';
        if (isVector(l) || isVector(r)) return fail('On ne compare pas des vecteurs avec `<` / `>` : compare leurs composantes (`.x`) ou leur longueur (`.magnitude`).');
        return fail();
      case '==':
      case '!=':
        if ((isNumeric(l) && isNumeric(r)) || (l === 'bool' && r === 'bool') || (isVector(l) && isVector(r) && l === r)) return 'bool';
        return fail();
      case '&&':
      case '||':
        if (l === 'bool' && r === 'bool') return 'bool';
        return fail(isNumeric(l) || isNumeric(r) ? 'Chaque côté doit être une comparaison : `h != 0f && v != 0f`.' : undefined);
      default:
        return 'unknown';
    }
  }

  /** Type de la cible d'une affectation / incrémentation, avec les erreurs pédagogiques CS1612 / CS0200. */
  private typeOfAssignTarget(target: Expr, op: string): SimType {
    if (target.kind === 'error') return 'unknown';
    if (target.kind === 'ident') {
      const info = this.scope.lookup(target.name);
      if (info) {
        if (this.inFieldInitializer) {
          this.error(syntaxError('Pas d\'affectation dans un initialiseur de champ.', target.line, target.column, { csharpCode: 'CS0236' }));
          return 'unknown';
        }
        if (op !== '=') info.used = true;
        target.type = info.type;
        return info.type;
      }
      if (API_ROOTS.has(target.name)) {
        this.error(syntaxError(`\`${target.name}\` ne peut pas être remplacé : modifie \`${target.name}.position\`${target.name === 'transform' ? '' : ' (ou un autre membre)'}.`, target.line, target.column, { csharpCode: 'CS1656' }));
        return 'unknown';
      }
      const suggestion = suggest(target.name, this.scope.names());
      this.error(syntaxError(
        `La variable \`${target.name}\` n'est pas déclarée : en C#, il faut écrire son type devant la première fois (\`float ${target.name} = …;\`).`,
        target.line, target.column,
        { csharpCode: 'CS0103', hint: suggestion ? `Tu voulais peut-être écrire \`${suggestion}\` ?` : undefined },
      ));
      return 'unknown';
    }
    if (target.kind === 'member') {
      const { object, name } = target;
      const root = this.rootOf(object);
      if (root) {
        if (root === 'transform' && name === 'position') {
          target.type = 'Vector3';
          return 'Vector3';
        }
        if (root === 'transform') {
          return this.unsupportedMember(`transform.${name}`, target, ['position'], 'Seul `transform.position` est modifiable dans cet exercice.');
        }
        this.error(syntaxError(`\`${root}.${name}\` est en lecture seule : on ne peut pas l'affecter.`, target.line, target.column, { csharpCode: 'CS0200' }));
        return 'unknown';
      }
      if (object.kind === 'member' && this.rootOf(object.object) === 'transform' && object.name === 'position' && ['x', 'y', 'z'].includes(name)) {
        this.error(syntaxError(
          `Impossible de modifier \`transform.position.${name}\` directement : \`position\` est une propriété qui renvoie une COPIE du \`Vector3\` (un struct). Modifier la copie ne changerait rien, alors C# refuse.`,
          target.line, target.column,
          {
            csharpCode: 'CS1612',
            hint: `Passe par une variable temporaire : \`Vector3 p = transform.position; p.${name} = …; transform.position = p;\` — ou reconstruis le vecteur : \`transform.position = new Vector3(…, …, …);\`.`,
          },
        ));
        return 'unknown';
      }
      const objType = this.typeOf(object);
      if (objType === 'unknown') return 'unknown';
      if (isVector(objType)) {
        if (['x', 'y', 'z'].includes(name)) {
          if (name === 'z' && objType === 'Vector2') {
            this.error(syntaxError('Un `Vector2` n\'a que `x` et `y`, pas de `z`.', target.line, target.column, { csharpCode: 'CS1061' }));
            return 'unknown';
          }
          if (object.kind !== 'ident') {
            this.error(syntaxError(`On ne peut modifier \`.${name}\` que sur une variable, pas sur le résultat d'un calcul.`, target.line, target.column, { csharpCode: 'CS1612', hint: 'Stocke d\'abord le vecteur dans une variable `Vector3`.' }));
            return 'unknown';
          }
          target.type = 'float';
          return 'float';
        }
        if (name in VECTOR_INSTANCE_MEMBERS) {
          this.error(syntaxError(`\`.${name}\` est en lecture seule : on ne peut pas l'affecter.`, target.line, target.column, { csharpCode: 'CS0200' }));
          return 'unknown';
        }
        this.error(syntaxError(`\`${objType}\` ne contient pas de membre \`${name}\`.`, target.line, target.column, { csharpCode: 'CS1061' }));
        return 'unknown';
      }
      this.error(syntaxError(`Un \`${objType}\` n'a pas de membre \`${name}\` à modifier.`, target.line, target.column, { csharpCode: 'CS1061' }));
      return 'unknown';
    }
    this.error(syntaxError('La partie gauche d\'une affectation doit être une variable.', target.line, target.column, { csharpCode: 'CS0131' }));
    return 'unknown';
  }

  private typeOfAssign(expr: Expr & { kind: 'assign' }): SimType {
    const targetType = this.typeOfAssignTarget(expr.target, expr.op);
    const valueType = this.typeOf(expr.value);
    if (targetType === 'unknown') return 'unknown';
    const targetName = describeTarget(expr.target);
    if (expr.op === '=') {
      this.checkAssignable(valueType, targetType, expr.value, targetName);
      return targetType;
    }
    const binOp = expr.op.slice(0, -1) as BinaryOp;
    const resultType = this.binaryType(binOp, targetType, valueType, expr);
    if (resultType !== 'unknown') this.checkAssignable(resultType, targetType, expr.value, targetName);
    return targetType;
  }
}

// ---------------------------------------------------------------------- utilitaires

function describeTarget(target: Expr): string {
  if (target.kind === 'ident') return target.name;
  if (target.kind === 'member') return `${describeTarget(target.object)}.${target.name}`;
  return 'la cible';
}

function exampleFor(root: string): string {
  switch (root) {
    case 'transform': return 'transform.position';
    case 'Time': return 'Time.deltaTime';
    case 'Input': return 'Input.GetAxisRaw("Horizontal")';
    case 'Mathf': return 'Mathf.Clamp(x, -8f, 8f)';
    case 'Vector3': return 'Vector3.right';
    case 'Vector2': return 'Vector2.right';
    case 'KeyCode': return 'KeyCode.UpArrow';
    default: return 'Debug.Log("…")';
  }
}

/** Suggestion « tu voulais peut-être écrire … ? » : casse différente, ou distance de Levenshtein ≤ 2. */
export function suggest(name: string, candidates: Iterable<string>): string | undefined {
  const lower = name.toLowerCase();
  let best: string | undefined;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    if (candidate === name) continue;
    if (candidate.toLowerCase() === lower) return candidate;
    if (name.length < 3) continue;
    const d = levenshtein(lower, candidate.toLowerCase());
    const max = name.length >= 6 ? 2 : 1;
    if (d <= max && d < bestDistance) {
      best = candidate;
      bestDistance = d;
    }
  }
  return best;
}

function levenshtein(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 2) return 3;
  const prev = new Array<number>(b.length + 1);
  const curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

// ---------------------------------------------------------------------- usesDeltaTime (analyse de flot)

/**
 * `Time.deltaTime` « teinte » toute variable qui en dérive (transitivement, y compris via les champs
 * d'une frame à l'autre) ; le programme utilise deltaTime si une valeur teintée atteint
 * `transform.position` (affectation) ou `transform.Translate(...)`. Sur-approximation monotone :
 * une variable teintée le reste (un `x = 0f;` ultérieur ne la « nettoie » pas) — suffisant pour
 * expliquer le résultat de la vérification, qui reste basée sur la mesure.
 */
export function computeUsesDeltaTime(program: Program): boolean {
  const tainted = new Set<string>();
  let found = false;

  const isTransformMember = (e: Expr, member: string): boolean =>
    e.kind === 'member' && e.object.kind === 'ident' && e.object.name === 'transform' && e.name === member;

  const mentions = (e: Expr | undefined): boolean => {
    if (!e) return false;
    switch (e.kind) {
      case 'member':
        if (e.object.kind === 'ident' && e.object.name === 'Time' && e.name === 'deltaTime') return true;
        return mentions(e.object);
      case 'ident':
        return tainted.has(e.name);
      case 'call':
        return mentions(e.callee) || e.args.some(mentions);
      case 'new':
        return e.args.some(mentions);
      case 'unary':
        return mentions(e.operand);
      case 'binary':
        return mentions(e.left) || mentions(e.right);
      case 'assign':
        return mentions(e.value);
      case 'update':
        return mentions(e.target);
      case 'conditional':
        return mentions(e.test) || mentions(e.consequent) || mentions(e.alternate);
      case 'cast':
        return mentions(e.expr);
      default:
        return false;
    }
  };

  const visitExpr = (e: Expr | undefined): void => {
    if (!e) return;
    switch (e.kind) {
      case 'assign': {
        const valueTainted = mentions(e.value);
        if (isTransformMember(e.target, 'position')) {
          if (valueTainted) found = true;
        } else if (e.target.kind === 'ident') {
          if (valueTainted) tainted.add(e.target.name);
        } else if (e.target.kind === 'member' && e.target.object.kind === 'ident') {
          if (valueTainted) tainted.add(e.target.object.name);
        }
        visitExpr(e.value);
        return;
      }
      case 'call':
        if (isTransformMember(e.callee, 'Translate') && e.args.some(mentions)) found = true;
        e.args.forEach(visitExpr);
        return;
      case 'binary':
        visitExpr(e.left);
        visitExpr(e.right);
        return;
      case 'unary':
        visitExpr(e.operand);
        return;
      case 'conditional':
        visitExpr(e.test);
        visitExpr(e.consequent);
        visitExpr(e.alternate);
        return;
      case 'cast':
        visitExpr(e.expr);
        return;
      case 'new':
        e.args.forEach(visitExpr);
        return;
      default:
        return;
    }
  };

  const visitStmt = (s: Stmt): void => {
    switch (s.kind) {
      case 'block':
        s.body.forEach(visitStmt);
        return;
      case 'vardecl':
        for (const d of s.declarators) if (d.init && mentions(d.init)) tainted.add(d.name);
        for (const d of s.declarators) visitExpr(d.init);
        return;
      case 'expr':
        visitExpr(s.expr);
        return;
      case 'if':
        visitExpr(s.test);
        visitStmt(s.consequent);
        if (s.alternate) visitStmt(s.alternate);
        return;
      case 'for':
        if (s.init) visitStmt(s.init);
        visitExpr(s.test);
        s.update.forEach(visitExpr);
        visitStmt(s.body);
        return;
      case 'while':
        visitExpr(s.test);
        visitStmt(s.body);
        return;
      default:
        return;
    }
  };

  for (const field of program.fields) if (field.init && mentions(field.init)) tainted.add(field.name);
  // Trois passes : la teinte peut remonter d'une affectation tardive vers une lecture antérieure (frame suivante).
  for (let pass = 0; pass < 3 && !found; pass++) {
    for (const name of LIFECYCLE_METHODS) {
      const method = program.methods[name];
      if (method) visitStmt(method.body);
    }
  }
  return found;
}
